import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Container, Row, Col } from "react-bootstrap";
import HelmetWrapper from "../../HelmetWrapper";
import PageHeadingNew from "../../components/common-components/custom-comps/PageHeadingNew";
import TableSkeleton from "../../components/common-components/TableSkeleton";
import PaymentLinkTable from "../../components/payment-link-system/PaymentLinkTable";
import FilterContainer from "../../components/common-components/filters/filter-container";
import DateRangerFilter from "../../components/common-components/filters/date-ranger-filter";
import { selectAuth } from "../../components/auth/auth.selector";
import { fetchFranchiseLeadsData } from "../../redux/website-reducers/websiteSlice";
import { formatTimezoneThenStraight1 } from "../../components/common-data/Converts";
import { CallIcon, EmailIcon } from "../../images/icons/icons";
import "../../styles/lead-mangement/academy-leads.css";

const FranchiseLeads = () => {
  const dispatch = useDispatch();
  const authData = useSelector(selectAuth);
  const userInfoData = authData.user;

  const { franchiseLeadsLoading, franchiseLeads } = useSelector(
    (state) => state.webApi
  );
  const { data, pagination } = franchiseLeads || {};

  const [isPage, setIsPage] = useState(0);
  const [pageLimit, setPageLimit] = useState(10);
  const [searchInput, setSearchInput] = useState("");
  const [cityFilter, setCityFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const iniFilters = {
    page: 1,
    limit: 10,
    search: "",
    city: "",
    status: "",
  };
  const [filters, setFilters] = useState(iniFilters);

  // Fetch data when filters change
  useEffect(() => {
    dispatch(
      fetchFranchiseLeadsData({
        filters: filters,
        token: userInfoData?.token,
      })
    );
  }, [dispatch, filters, userInfoData?.token]);

  // Update filters when isPage or pageLimit changes
  useEffect(() => {
    setFilters((prevs) => ({
      ...prevs,
      page: isPage + 1,
      limit: pageLimit,
    }));
  }, [isPage, pageLimit]);

  // Debounce search and update filters
  useEffect(() => {
    const timer = setTimeout(() => {
      setFilters((prevs) => {
        if (searchInput !== prevs.search) {
          setIsPage(0);
          return { ...prevs, search: searchInput, page: 1 };
        }
        return prevs;
      });
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // Update filters when city filter changes
  useEffect(() => {
    const timer = setTimeout(() => {
      setFilters((prevs) => {
        if (cityFilter !== prevs.city) {
          setIsPage(0);
          return { ...prevs, city: cityFilter, page: 1 };
        }
        return prevs;
      });
    }, 300);
    return () => clearTimeout(timer);
  }, [cityFilter]);

  // Update filters when status filter changes
  useEffect(() => {
    setFilters((prevs) => {
      if (statusFilter !== prevs.status) {
        setIsPage(0);
        return { ...prevs, status: statusFilter, page: 1 };
      }
      return prevs;
    });
  }, [statusFilter]);

  // Update filters when date range changes
  useEffect(() => {
    setFilters((prevs) => {
      const newFilters = { ...prevs };
      if (startDate) {
        newFilters.from_date = startDate;
      } else {
        delete newFilters.from_date;
      }
      if (endDate) {
        newFilters.to_date = endDate;
      } else {
        delete newFilters.to_date;
      }
      return newFilters;
    });
  }, [startDate, endDate]);

  const handleClearFilters = () => {
    setSearchInput("");
    setCityFilter("");
    setStatusFilter("");
    setStartDate("");
    setEndDate("");
    setFilters(iniFilters);
    setIsPage(0);
  };

  // Handle date range change
  const handleDateRangeChange = (start, end) => {
    setStartDate(start);
    setEndDate(end);
  };

  const tbColumns = [
    {
      dataField: "name",
      text: "Contact Details",
      formatter: (_, row) => (
        <div className="emeberDetailsRowContainer">
          <div className="d-flex flex-column gap-1">
            {row?.name && (
              <div className="d-flex align-items-center gap-2">
                <span className="tb-data fw-semibold">{row?.name}</span>
              </div>
            )}
            <div className="d-flex align-items-center gap-2">
              <CallIcon color="#6c757d" />
              <small className="tb-data small">{row?.mobile || "NA"}</small>
            </div>
            <div className="d-flex align-items-center gap-2">
              <EmailIcon color="#6c757d" />
              <small
                className="tb-data small"
                style={{ textTransform: "lowercase" }}
              >
                {row?.email || "NA"}
              </small>
            </div>
          </div>
        </div>
      ),
    },
    {
      dataField: "city",
      text: "City",
      formatter: (_, row) => (
        <span className="tb-data">
          <span className="badge bg-light text-dark border">
            {row?.city || "NA"}
          </span>
        </span>
      ),
    },
    {
      dataField: "planning_timeline",
      text: "Planning Timeline",
      formatter: (_, row) => (
        <span className="tb-data">
          <span className="small">{row?.planning_timeline || "NA"}</span>
        </span>
      ),
    },
    {
      dataField: "utm_source",
      text: "UTM Source",
      formatter: (_, row) => (
        <span className="tb-data">
          <span className="small text-muted">{row?.utm_source || "NA"}</span>
        </span>
      ),
    },
    {
      dataField: "ga_source",
      text: "GA Source",
      formatter: (_, row) => (
        <span className="tb-data">
          <span className="small text-muted">{row?.ga_source || "NA"}</span>
        </span>
      ),
    },
    {
      dataField: "status",
      text: "Status",
      formatter: (_, row) => {
        const isActive = row?.status === "active";
        return (
          <span className="tb-data">
            <span
              className="badge"
              style={{
                backgroundColor: isActive ? "#d1fae5" : "#fee2e2",
                color: isActive ? "#065f46" : "#991b1b",
                padding: "6px 12px",
                borderRadius: "6px",
                fontWeight: "500",
                textTransform: "capitalize",
              }}
            >
              {row?.status || "NA"}
            </span>
          </span>
        );
      },
    },
    {
      dataField: "date_added",
      text: "Date Added",
      formatter: (_, row) => (
        <span className="tb-data">
          <span className="small text-muted">
            {row?.date_added
              ? formatTimezoneThenStraight1(row.date_added)
              : "NA"}
          </span>
        </span>
      ),
    },
  ];

  // Render filters component
  const renderFilters = () => {
    return (
      <Row className="mb-4">
        <Col>
          <FilterContainer onReset={handleClearFilters}>
            {/* Search Filter */}
            <div className="filter-search-wrapper">
              <input
                type="text"
                placeholder="Search by name, mobile, email..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className="premium-search-input"
              />
            </div>

            {/* City Filter */}
            <div className="filter-search-wrapper">
              <input
                type="text"
                placeholder="Filter by city..."
                value={cityFilter}
                onChange={(e) => setCityFilter(e.target.value)}
                className="premium-search-input"
              />
            </div>

            {/* Date Range Filter */}
            <DateRangerFilter
              startDate={startDate}
              endDate={endDate}
              onDateChange={handleDateRangeChange}
            />

            {/* Status Filter */}
            <div className="filter-search-wrapper">
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="premium-search-input"
              >
                <option value="">All Status</option>
                <option value="active">Active</option>
                <option value="deleted">Deleted</option>
              </select>
            </div>
          </FilterContainer>
        </Col>
      </Row>
    );
  };

  return (
    <HelmetWrapper title="WTF: Franchise Leads">
      <Container className="bg-cloud-k px-50px" fluid>
        <Row>
          <Col>
            <PageHeadingNew heading="Franchise Leads" />
          </Col>
        </Row>

        {/* Filters Section */}
        {renderFilters()}

        <Row className="mt-4">
          <Col>
            <div className="fullWidthFlexCenter overflowAuto separatedTable no-border-table mod-height-100vh">
              {franchiseLeadsLoading ? (
                <TableSkeleton />
              ) : (
                <PaymentLinkTable
                  tableData={data || []}
                  tableColumns={tbColumns}
                  tablePagination={pagination}
                  isPage={isPage}
                  setIsPage={setIsPage}
                  pageLimit={pageLimit}
                  setPageLimit={setPageLimit}
                />
              )}
            </div>
          </Col>
        </Row>
      </Container>
    </HelmetWrapper>
  );
};

export default FranchiseLeads;
